import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import ChartHeader from "./ChartHeader";

function ChartOKNOK({ title, subtitle, data, detailsDesc, orderNumber = "", dateRange = "" }) {
    const [hoveredSegment, setHoveredSegment] = useState(null);
    const [showDetailChart, setShowDetailChart] = useState(false);
    const [detailHoveredSegment, setDetailHoveredSegment] = useState(null);

    // Przykładowe dane - w prawdziwej aplikacji byłyby przekazane jako props
    const defaultData = {
        totalOK: "N/A",
        nokDetails: {
            A: "N/A"
        },
        nok: "N/A"
    };

    const inputData = data || defaultData;
    // Oblicz total NOK z wszystkich typów
    const totalOK = inputData.totalOK || 0;  // Parts OK z nad wykresu
    const NOK = inputData.nokDetails?.mainField || 0;  // Parts NOK z nad wykresu
    const totalChecked = totalOK + NOK;  // Total Checked z nad wykresu

    // Suma z NOK details (do tooltipa, nie do legendy)
    const totalNOKDetails = Object.entries(inputData.nokDetails || {})
        .filter(([key]) => key !== 'mainField')
        .reduce((sum, [, value]) => sum + value, 0);

    // ===== PROCENTY DLA LEGENDY =====
    // OK: Parts OK / Total Checked
    const okPercentage = totalChecked > 0 ? Math.round((totalOK / totalChecked) * 100 * 10) / 10 : 0;

    // NOK: Parts NOK / Total Checked
    const nokPercentage = totalChecked > 0 ? Math.round((NOK / totalChecked) * 100 * 10) / 10 : 0;

    // ===== DANE DLA WYKRESU (pie chart) =====
    const mainChartData = [
        {
            name: 'OK',
            value: totalOK,
            percentage: okPercentage,
            displayPercentage: okPercentage  // Dla legendy
        },
        {
            name: 'NOK',
            value: NOK,  // Parts NOK
            percentage: nokPercentage,
            displayPercentage: nokPercentage,  // Dla legendy
            details: inputData.nokDetails || {}
        }
    ];

    // Dane dla szczegółowego wykresu NOK
    const nokDetailData = Object.entries(inputData.nokDetails || {})
        .filter(([key, value]) => value > 0 && key !== 'mainField')
        .map(([name, count]) => ({
            name,
            value: count,
            percentage: totalNOKDetails > 0 ? Math.round((count / totalNOKDetails) * 100 * 10) / 10 : 0
        }));

    const colors = {
        'OK': '#4CAF50',
        'NOK': '#F44336'
    };

    const nokColors = {
        'A': '#008B8B', 'B': '#FF7F00', 'C': '#800080', 'D': '#000080',
        'E': '#FBCEB1', 'F': '#FFD700', 'G': '#20B2AA', 'H': '#4B0082',
        'I': '#32CD32', 'J': '#C71585', 'K': '#FA8072', 'L': '#008000',
        'M': '#87CEEB', 'N': '#FF69B4', 'O': '#00CED1', 'P': '#DC143C'
    };

    const isVerySmallNOK = (percentage) => percentage <= 3;

    const shouldShowDetailChart = hoveredSegment &&
        hoveredSegment.name === 'NOK' &&
        isVerySmallNOK(hoveredSegment.percentage) &&
        showDetailChart;

    // Custom tooltip dla głównego wykresu
    const MainCustomTooltip = ({ active, payload }) => {
        // Nie wyświetlaj gdy szczegółowy wykres jest aktywny
        if (shouldShowDetailChart) {
            return null;
        }

        if (active && payload && payload.length) {
            const data = payload[0].payload;

            // Dla NOK - specjalny format
            if (data.name === 'NOK') {
                const defectDetails = Object.entries(data.details || {})
                    .filter(([key, value]) => key !== 'mainField' && value > 0);

                return (
                    <div style={{
                        backgroundColor: 'white',
                        padding: '12px',
                        border: '1px solid #ccc',
                        borderRadius: '6px',
                        fontSize: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        minWidth: '240px',
                        zIndex: 1000,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}>
                        {/* GÓRNY WIERSZ: Główne info */}
                        <div style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: 'rgb(52, 71, 103)',
                            marginBottom: '8px',
                            lineHeight: '1.4'
                        }}>
                            Total NOK: {data.value} parts
                        </div>
                        <div style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: 'rgb(52, 71, 103)',
                            marginBottom: '8px',
                            lineHeight: '1.4'
                        }}>
                            Percentage: {data.percentage}%
                        </div>
                        <div style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: 'rgb(52, 71, 103)',
                            marginBottom: '8px',
                            lineHeight: '1.4'
                        }}>
                            Total defects: {totalNOKDetails}
                        </div>

                        {/* SEPARATOR */}
                        <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #eee' }} />

                        {/* DEFECT DETAILS */}
                        {defectDetails.length > 0 && (
                            <div style={{ marginBottom: '8px' }}>
                                <p style={{
                                    margin: '0 0 6px 0',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#666'
                                }}>
                                    Defect details:
                                </p>

                                {defectDetails.map(([nokType, count]) => {
                                    const description = detailsDesc?.find(el => el.label === nokType)?.value || nokType;

                                    return (
                                        <div key={nokType} style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            marginBottom: '3px',
                                            fontSize: '11px',
                                            padding: '2px 0',
                                            color: '#333'
                                        }}>
                                            <span>{nokType}: {description}</span>
                                            <span style={{ fontWeight: '600', marginLeft: '16px' }}>{count}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                    </div>
                );
            }

            // Dla OK - prosty tooltip
            return (
                <div style={{
                    backgroundColor: 'white',
                    padding: '12px',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    fontSize: '13px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '180px',
                    zIndex: 1000
                }}>
                    <p style={{
                        margin: '0',
                        fontWeight: '600',
                        color: 'rgb(52, 71, 103)'
                    }}>
                        {data.name}: {data.value} parts ({data.percentage}%)
                    </p>
                </div>
            );
        }
        return null;
    };

    // ===== TOOLTIP SZCZEGÓŁOWY =====
    const DetailCustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            const description = detailsDesc?.find(el => el.label === data.name)?.value || data.name;

            return (
                <div style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    zIndex: 1002,
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap'
                }}>
                    {data.name}: {description} - {data.value} parts ({data.percentage}%)
                </div>
            );
        }
        return null;
    };

    // ===== DYNAMICZNA LEGENDA - Z PRAWIDŁOWYMI WARTOŚCIAMI =====
    const CustomLegend = ({ payload }) => {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '24px',
                marginTop: '20px',
                fontSize: '13px',
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}>
                {/* OK - Parts OK / Total Checked */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <div style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: '#4CAF50',
                        borderRadius: '2px'
                    }}></div>
                    <span style={{ color: 'rgb(52, 71, 103)', fontWeight: '500' }}>
                        OK: {totalOK} ({okPercentage}%)
                    </span>
                </div>

                {/* NOK - Parts NOK / Total Checked */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <div style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: '#F44336',
                        borderRadius: '2px'
                    }}></div>
                    <span style={{ color: 'rgb(52, 71, 103)', fontWeight: '500' }}>
                        NOK: {NOK} ({nokPercentage}%)
                    </span>
                </div>
            </div>
        );
    };

    const handleMainMouseEnter = (data, event) => {
        setHoveredSegment(data);
        if (data.name === 'NOK' && isVerySmallNOK(data.percentage)) {
            setShowDetailChart(true);
        }
    };

    const handleMainMouseLeave = () => {
        // Nie resetuj od razu jeśli pokazujemy szczegółowy wykres
        if (!shouldShowDetailChart) {
            setHoveredSegment(null);
            setShowDetailChart(false);
        }
    };

    // Handle mouse events dla szczegółowego wykresu
    const handleDetailMouseEnter = (data) => {
        setDetailHoveredSegment(data);
    };

    const handleDetailMouseLeave = () => {
        setDetailHoveredSegment(null);
        setShowDetailChart(false);
        setHoveredSegment(null);
    };

    const styles = {
        container: {
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            padding: '24px',
            margin: '16px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid rgba(0, 0, 0, 0.06)'
        },
        header: {
            marginBottom: '24px'
        },
        chartContainer: {
            backgroundColor: 'rgb(248, 249, 250)',
            borderRadius: '6px',
            padding: '24px',
            marginBottom: '28px',
            minHeight: '412px',
            display: 'flex',
            flexDirection: 'column'
        },
        statsContainer: {
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            marginBottom: '16px',
            padding: '0 20px'
        },
        statItem: {
            textAlign: 'center'
        },
        statNumber: {
            fontSize: '20px',
            fontWeight: '600',
            color: 'rgb(52, 71, 103)',
            margin: '0',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
        },
        statLabel: {
            fontSize: '13px',
            color: 'rgb(123, 128, 154)',
            margin: '0',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
        },
        detailChart: {
            position: 'absolute',
            right: '20px',
            top: '20px',
            width: '300px',
            height: '300px',
            backgroundColor: 'white',
            border: '2px solid #ddd',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            zIndex: 1001,
            padding: '16px',
            display: 'flex',
            flexDirection: 'column'
        },
        detailHeader: {
            fontSize: '14px',
            fontWeight: '600',
            color: 'rgb(52, 71, 103)',
            marginBottom: '8px',
            textAlign: 'center',
            borderBottom: '1px solid #eee',
            paddingBottom: '8px'
        },
        detailSubtitle: {
            fontSize: '12px',
            color: 'rgb(52, 71, 103)',
            marginBottom: '16px',
            textAlign: 'center',
            fontWeight: '600'
        }
    };

    return (
        <div style={styles.container}>
            <ChartHeader
                title={title}
                subtitle={subtitle}
                orderName={orderNumber}
                dateRange={dateRange}
            />

            <div style={styles.chartContainer}>
                <div style={styles.statsContainer}>
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>{totalOK}</p>
                        <p style={styles.statLabel}>Parts OK</p>
                    </div>
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>{NOK}</p>
                        <p style={styles.statLabel}>Parts NOK</p>
                    </div>
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>{totalChecked}</p>
                        <p style={styles.statLabel}>Total Checked</p>
                    </div>
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>
                            {totalChecked > 0 ? ((totalOK / totalChecked) * 100).toFixed(1) : 0}%
                        </p>
                        <p style={styles.statLabel}>Pass Rate</p>
                    </div>
                </div>

                <div style={{ position: 'relative' }}>
                    {/* Główny wykres */}
                    <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                            <Pie
                                data={mainChartData}
                                cx="50%"
                                cy="50%"
                                innerRadius={40}
                                outerRadius={80}
                                stroke="none"
                                dataKey="value"
                                onMouseEnter={handleMainMouseEnter}
                                onMouseLeave={handleMainMouseLeave}
                            >
                                {mainChartData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={colors[entry.name]}
                                        stroke={hoveredSegment?.name === entry.name ? '#333' : 'none'}
                                        strokeWidth={hoveredSegment?.name === entry.name ? 2 : 0}
                                    />
                                ))}
                            </Pie>
                            <Tooltip content={<MainCustomTooltip />} />
                            <Legend content={<CustomLegend />} />
                        </PieChart>
                    </ResponsiveContainer>

                    {/* Szczegółowy wykres NOK */}
                    {shouldShowDetailChart && (
                        <div
                            style={styles.detailChart}
                            onMouseLeave={handleDetailMouseLeave}
                        >
                            <div style={styles.detailHeader}>
                                🔍 NOK Details
                            </div>
                            <div style={styles.detailSubtitle}>
                                Total NOK: {NOK} parts ({nokPercentage}%)
                            </div>

                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={nokDetailData}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        paddingAngle={2}
                                        dataKey="value"
                                        onMouseEnter={handleDetailMouseEnter}
                                    >
                                        {nokDetailData.map((entry, index) => (
                                            <Cell
                                                key={`detail-cell-${index}`}
                                                fill={nokColors[entry.name] || '#999'}
                                                stroke={detailHoveredSegment?.name === entry.name ? '#333' : 'white'}
                                                strokeWidth={detailHoveredSegment?.name === entry.name ? 2 : 1}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<DetailCustomTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* Wskazówka dla małych segmentów NOK */}
                    {hoveredSegment?.name === 'NOK' &&
                        isVerySmallNOK(hoveredSegment.percentage) &&
                        !shouldShowDetailChart && (
                            <div style={{
                                fontSize: '11px',
                                color: '#999',
                                textAlign: 'center',
                                marginTop: '8px',
                                fontStyle: 'italic'
                            }}>
                                💡 Hover over NOK area for detailed breakdown
                            </div>
                        )}
                </div>
            </div>
        </div>
    );
}

export default ChartOKNOK;